import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

// Suprimir warnings de Apollo Client en producción
if (import.meta.env.PROD) {
    // Silenciar warnings específicos de APIs obsoletas en producción
    const originalWarn = console.warn;
    console.warn = (...args: any[]) => {
        // Filtrar warnings específicos de Apollo DevTools
        const message = args.join(' ');
        if (message.includes('go.apollo.dev/c/err') || 
            message.includes('connectToDevTools') ||
            message.includes('clientAware')) {
            return; // Ignorar estos warnings en producción
        }
        originalWarn.apply(console, args);
    };
}

const rawEnvUrl = import.meta.env && import.meta.env.VITE_GRAPHQL_DELIVERY ? String(import.meta.env.VITE_GRAPHQL_DELIVERY) : '';
let envUrl = rawEnvUrl.replace(/^"|"$/g, '');

// Si la URL absoluta proporcionada no incluye el sufijo '/query', añadirlo.
if (envUrl && /^https?:\/\//.test(envUrl)) {
    envUrl = envUrl.replace(/\/+$/, '');
    if (!envUrl.endsWith('/query')) envUrl = envUrl + '/query';
}

const graphqlUri = '/graphql';

// Variable global para almacenar el token dinámico
let currentAuthToken: string | null = null;

// Función para actualizar el token dinámicamente
export const setAuthToken = (token: string | null) => {
    currentAuthToken = token;
};

// Función para obtener el token actual
export const getAuthToken = (): string | null => {
    return currentAuthToken;
};

// Enlace HTTP para todas las operaciones (queries, mutations y subscriptions)
const httpLink = createHttpLink({ 
    uri: graphqlUri,
});

// Manejo de errores de autenticación
const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        for (const err of graphQLErrors) {
            // Detectar errores de autenticación (401, 403, token inválido/expirado)
            if (
                err.extensions?.code === 'UNAUTHENTICATED' ||
                err.extensions?.code === 'FORBIDDEN' ||
                err.message?.toLowerCase().includes('unauthorized') ||
                err.message?.toLowerCase().includes('token') ||
                err.message?.toLowerCase().includes('jwt')
            ) {
                console.error('❌ [Apollo] Token expirado o inválido. Notificando al frontend padre...');
                
                const hadToken = !!currentAuthToken;
                // Limpiar token
                currentAuthToken = null;
                
                // Notificar al frontend padre solo si teníamos token (no por race condition)
                if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                    (window as any).microfrontendAuth.notifyTokenExpired();
                }
                return;
            }
        }
    }

    if (networkError) {
        // Detectar errores de red relacionados con autenticación
        const statusCode = (networkError as any)?.statusCode;
        if (statusCode === 401 || statusCode === 403) {
            console.error('❌ [Apollo] Error de autenticación (HTTP', statusCode, '). Notificando al frontend padre...');
            
            const hadToken = !!currentAuthToken;
            // Limpiar token
            currentAuthToken = null;
            
            // Notificar al frontend padre solo si teníamos token (no por race condition)
            if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                (window as any).microfrontendAuth.notifyTokenExpired();
            }
        }
    }
});

// Configurar el contexto de autenticación
const authLink = setContext(async (operation, { headers }) => {
    let token = currentAuthToken;

    // Si aún no hay token, esperar a que el frontend padre lo envíe (evita race condition 401)
    if (!token && typeof window !== 'undefined' && (window as any).microfrontendAuth?.waitForToken) {
        try {
            token = await (window as any).microfrontendAuth.waitForToken(5000);
            if (token) currentAuthToken = token;
        } catch {
            // Timeout: proceder sin token
        }
    }

    // Solo mostrar warning si no hay token para operaciones que lo requieren
    if (!token && operation.operationName !== 'IntrospectionQuery') {
        console.warn('⚠️ No hay token disponible para operación:', operation.operationName);
    }

    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
            'content-type': 'application/json',
        }
    }
});

const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {

            }
        }
    }
});

const client = new ApolloClient({
    link: errorLink.concat(authLink.concat(httpLink)),
    cache,
    defaultOptions: {
        watchQuery: {
            errorPolicy: 'all',
            fetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true
        },
        query: {
            errorPolicy: 'all',
            fetchPolicy: 'cache-first',
            // Timeout específico para queries
            context: {
                timeout: 15000
            }
        },
        mutate: {
            errorPolicy: 'all',
            // Timeout específico para mutations
            context: {
                timeout: 20000
            }
        }
    },
    // Configuración condicional completa según el entorno
    ...(import.meta.env.DEV ? {
        // Solo en desarrollo: habilitar DevTools
        devtools: {
            enabled: typeof window !== 'undefined'
        }
    } : {
        // En producción: configuración optimizada sin DevTools
        assumeImmutableResults: true,
        name: 'MicrofrontendApolloClient',
        version: '1.0',
        // Explícitamente desactivar DevTools en producción
        devtools: {
            enabled: false
        }
    })
});

export default client;
